import { useRef } from "react";
import { useHover } from "react-aria";

import { ColoredLabel, Tooltip, TooltipIntent, useTooltip } from "widgets";

import { capitalizeFirstLetter } from "../../../../app-utils/string.js";
import { PASSWORD_STATUS } from "../../../../settings/settings.js";
import { type IAgGridCellProps } from "../cell-base-interfaces.js";
import { TableCell } from "./core/TableCell.js";

export function PasswordStatusTagWidget({ passwordStatus }: { passwordStatus: PASSWORD_STATUS }) {
  const triggerRef = useRef(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const isDisabled = !passwordStatus.startsWith(PASSWORD_STATUS.EXPIRED);

  const { arrowProps, tooltipProps, state } = useTooltip(triggerRef, tooltipRef, {
    placement: "right",
    isDisabled,
    delay: 300,
  });
  const { hoverProps } = useHover({
    onHoverChange(isHovering) {
      if (!isDisabled) {
        if (isHovering) {
          state.open();
        } else {
          state.close();
        }
      }
    },
  });

  let tagColor = "#E0E0E4";

  if (passwordStatus.startsWith(PASSWORD_STATUS.EXPIRED)) {
    tagColor = "#CB5271";
  } else if (passwordStatus.startsWith(PASSWORD_STATUS.LOCKED)) {
    tagColor = "#F1C044";
  } else if (passwordStatus === PASSWORD_STATUS.OPEN) {
    tagColor = "#98C375";
  }

  return (
    <TableCell>
      <ColoredLabel color={tagColor} ref={triggerRef} domProps={hoverProps}>
        {capitalizeFirstLetter(passwordStatus.toLowerCase())}
      </ColoredLabel>

      {state.isOpen && (
        <Tooltip
          domProps={tooltipProps}
          arrowProps={arrowProps}
          ref={tooltipRef}
          content={"The account password has expired or has not been set"}
          intent={TooltipIntent.Danger}
        />
      )}
    </TableCell>
  );
}

export function PasswordStatusCell(props: IAgGridCellProps) {
  if (!props.value) return null;
  return <PasswordStatusTagWidget passwordStatus={props.value} />;
}

export default PasswordStatusCell;
